// Mappa live posizioni tecnici — caricamento iniziale REST + aggiornamenti WebSocket
import 'leaflet/dist/leaflet.css'
import { useState, useEffect, useRef } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import { useQuery } from '@tanstack/react-query'
import L from 'leaflet'
import { Navigation2, Radio, Clock } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Avatar } from '../../components/ui/Avatar'
import { useRealtimeStore, type TechnicianPosition } from '../../stores/realtime.store'
import api from '../../lib/api'
import { formatDate } from '../../lib/utils'

// Fix icone Leaflet con webpack/vite
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
})

function createTechIcon(isCheckedIn: boolean) {
  return L.divIcon({
    html: `<div style="
      width:38px;height:38px;
      background:${isCheckedIn ? '#0F52BA' : '#6B7280'};
      border:3px solid white;
      border-radius:50%;
      box-shadow:0 2px 8px rgba(0,0,0,0.4);
      display:flex;align-items:center;justify-content:center;
    "></div>`,
    className: '',
    iconSize: [38, 38],
    iconAnchor: [19, 19],
  })
}

// Centra mappa su tecnico selezionato
function MapFlyTo({ position }: { position: [number, number] | null }) {
  const map = useMap()
  const prevRef = useRef<[number, number] | null>(null)
  useEffect(() => {
    if (position && position !== prevRef.current) {
      prevRef.current = position
      map.flyTo(position, 14, { duration: 1.2 })
    }
  }, [position, map])
  return null
}

export function LiveMapPage() {
  const { technicianPositions, updateTechnicianPosition, connectionStatus } = useRealtimeStore()
  const [selectedTech, setSelectedTech] = useState<string | null>(null)

  // Caricamento iniziale posizioni note da REST
  const { data: initialPositions = [] } = useQuery<TechnicianPosition[]>({
    queryKey: ['geolocation', 'positions', 'all'],
    queryFn: async () => {
      try {
        const res = await api.get<TechnicianPosition[]>('/geolocation/positions/all')
        return res.data ?? []
      } catch {
        return []
      }
    },
    staleTime: 30_000,
  })

  // Popola lo store con le posizioni iniziali (una volta sola)
  useEffect(() => {
    for (const pos of initialPositions) {
      if (!technicianPositions[pos.userId]) {
        updateTechnicianPosition(pos)
      }
    }
  }, [initialPositions]) // eslint-disable-line react-hooks/exhaustive-deps

  const positions = Object.values(technicianPositions)
  const activeCount = positions.filter((p) => p.isCheckedIn).length

  const selectedPos = selectedTech
    ? technicianPositions[selectedTech]
    : null

  const flyTarget: [number, number] | null = selectedPos
    ? [selectedPos.coords.lat, selectedPos.coords.lng]
    : null

  return (
    <div className="space-y-4">
      <PageHeader
        title="Mappa live tecnici"
        subtitle={`${activeCount} tecnici in campo`}
        actions={
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className={`h-2.5 w-2.5 rounded-full ${connectionStatus === 'connected' ? 'bg-[var(--fc-success)] animate-pulse' : 'bg-[var(--fc-gray-400)]'}`} />
              <span className="text-sm text-[var(--fc-foreground-muted)]">
                {connectionStatus === 'connected' ? 'Live' : connectionStatus === 'connecting' ? 'Connessione...' : 'Disconnesso'}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-[var(--fc-foreground-muted)]">
              <Radio className="h-3.5 w-3.5" />
              <span>{positions.length} tecnici tracciati</span>
            </div>
          </div>
        }
      />

      <div className="flex gap-4">
        {/* Mappa */}
        <div className="flex-1 overflow-hidden rounded-lg border border-[var(--fc-border)]">
          <MapContainer
            center={[41.9028, 12.4964]}
            zoom={6}
            style={{ height: '600px', width: '100%' }}
            aria-label="Mappa live posizioni tecnici"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapFlyTo position={flyTarget} />
            {positions.map((tech) => (
              <Marker
                key={tech.userId}
                position={[tech.coords.lat, tech.coords.lng]}
                icon={createTechIcon(tech.isCheckedIn)}
                eventHandlers={{ click: () => setSelectedTech(tech.userId) }}
              >
                <Popup>
                  <div className="flex items-start gap-2 p-1 min-w-[180px]">
                    <Avatar name={tech.name} size="sm" online={tech.isCheckedIn} />
                    <div className="space-y-0.5">
                      <p className="font-semibold text-sm">{tech.name}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Navigation2 className="h-3 w-3" />
                        {tech.coords.lat.toFixed(5)}, {tech.coords.lng.toFixed(5)}
                      </p>
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(tech.updatedAt, 'HH:mm:ss')}
                      </p>
                      <Badge variant={tech.isCheckedIn ? 'success' : 'default'} dot>
                        {tech.isCheckedIn ? 'In campo' : 'Offline'}
                      </Badge>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Sidebar tecnici */}
        <div className="hidden lg:flex w-72 flex-col gap-3">
          <h3 className="text-sm font-semibold text-[var(--fc-foreground)]">
            Tecnici ({positions.length})
          </h3>

          {positions.length === 0 ? (
            <Card>
              <p className="text-sm text-[var(--fc-foreground-muted)] text-center py-4">
                Nessun tecnico tracciato.<br />
                <span className="text-xs">I tecnici devono inviare la posizione dall'app mobile.</span>
              </p>
            </Card>
          ) : (
            <div className="space-y-2 overflow-y-auto" style={{ maxHeight: 560 }}>
              {/* Attivi prima */}
              {[...positions]
                .sort((a, b) => (b.isCheckedIn ? 1 : 0) - (a.isCheckedIn ? 1 : 0))
                .map((tech) => (
                  <button
                    key={tech.userId}
                    className={`w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                      selectedTech === tech.userId
                        ? 'border-[var(--fc-primary)] bg-[color-mix(in_srgb,var(--fc-primary)_6%,transparent)]'
                        : 'border-[var(--fc-border)] bg-[var(--fc-surface)] hover:border-[var(--fc-primary)]'
                    }`}
                    onClick={() => setSelectedTech(tech.userId === selectedTech ? null : tech.userId)}
                    aria-pressed={selectedTech === tech.userId}
                  >
                    <Avatar name={tech.name} size="sm" online={tech.isCheckedIn} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-[var(--fc-foreground)]">{tech.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Badge variant={tech.isCheckedIn ? 'success' : 'default'} dot>
                          {tech.isCheckedIn ? 'In campo' : 'Offline'}
                        </Badge>
                      </div>
                      <p className="text-xs text-[var(--fc-foreground-muted)] mt-0.5 font-mono">
                        {tech.coords.lat.toFixed(4)}, {tech.coords.lng.toFixed(4)}
                      </p>
                      <p className="text-xs text-[var(--fc-foreground-muted)]">
                        {formatDate(tech.updatedAt, 'HH:mm:ss')}
                      </p>
                    </div>
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
